/**
 * rotateMnemonic.js — rotazione delle 24 parole mantenendo lo stesso @sigil.
 *
 * Genera una NUOVA mnemonica, deriva le nuove chiavi, le pubblica sul server
 * (rotateMnemonic callable), ri-cifra il backup PIN con la nuova mnemonica e
 * aggiorna lo store. Restituisce la nuova mnemonica da mostrare/salvare.
 *
 * Caveat (documentati in UI):
 *  - i contatti vedranno "chiave cambiata" (TOFU) → ri-verifica safety code;
 *  - i messaggi vecchi cifrati verso la vecchia box key non saranno piu'
 *    decifrabili;
 *  - gli altri device vanno ri-configurati con le nuove 24 parole.
 */

import { callFn } from './firebase';
import { newMnemonic, mnemonicToSeed } from './mnemonic';
import {
  initSodium, keypairFromSeed, boxKeypairFromSeed, deriveUid, toBase64Url,
} from './crypto';
import { encryptMnemonic, saveLastSigil } from './pinCrypto';
import { saveKeys } from './keys';
import { saveSession } from './storage';
import { publishMyKeysFromKeypairs } from './messaging';

/**
 * @param {object} p
 * @param {string} p.sigil       - il sigil corrente (resta invariato)
 * @param {string} p.pin         - PIN corrente (per ri-cifrare il backup)
 * @param {function} p.applyToStore - callback per aggiornare lo store zustand
 * @returns {Promise<{ ok:true, mnemonic:string }>}
 */
export async function rotateMnemonic({ sigil, pin, applyToStore }) {
  if (!sigil) throw new Error('sigil mancante');
  if (!pin || !/^[0-9]{6}$/.test(pin)) throw new Error('PIN a 6 cifre richiesto');

  await initSodium();
  const sigilLower = sigil.toLowerCase();

  // 1. Nuova mnemonica + chiavi
  const mnemonic = newMnemonic();
  const seed = mnemonicToSeed(mnemonic);
  const signKp = await keypairFromSeed(seed);
  const boxKp = await boxKeypairFromSeed(seed);
  const cryptoUid = await deriveUid(signKp.publicKey);
  const newPubKeyBox = await toBase64Url(boxKp.publicKey);
  const newPubKeySign = await toBase64Url(signKp.publicKey);

  // 2. Server: sostituisci le chiavi del sigil
  const res = await callFn('rotateMnemonic')({ sigil: sigilLower, newPubKeyBox, newPubKeySign });
  if (!res?.data?.ok) throw new Error(res?.data?.error || 'rotazione server fallita');

  // 3. Ri-cifra il backup con la nuova mnemonica e ricaricalo sul server
  const bundle = await encryptMnemonic({ pin, sigilLower, mnemonic });
  await callFn('setBackup')({
    sigilLower,
    blob: bundle.blob, salt: bundle.salt, nonce: bundle.nonce, argon2: bundle.argon2,
  });

  // 4. Ripubblica le chiavi pubbliche (idempotente)
  try { await publishMyKeysFromKeypairs(signKp.publicKey, boxKp.publicKey); } catch {}

  // 5. Persisti localmente
  saveLastSigil(sigil);
  try {
    await saveKeys({
      mnemonic, uid: cryptoUid, sigil,
      signPub: signKp.publicKey, signSec: signKp.secretKey,
      boxPub: boxKp.publicKey, boxSec: boxKp.secretKey,
      securityLevel: 1,
    });
  } catch {}
  try { saveSession({ mnemonic, uid: cryptoUid, sigil }); } catch {}

  // 6. Aggiorna lo store in memoria
  if (typeof applyToStore === 'function') {
    applyToStore({
      mnemonic,
      publicKey: signKp.publicKey, secretKey: signKp.secretKey,
      uid: cryptoUid,
      boxPub: boxKp.publicKey, boxSec: boxKp.secretKey,
    });
  }

  return { ok: true, mnemonic };
}
